// ── Agent SDK — Zod schemas (source of truth for data types) ─────────────────
// Function-bearing types (PlatformContext, NavigationAPI, PlatformState)
// remain hand-written in index.ts since Zod can't model functions.

import { z } from 'zod'

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
})

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  plan: z.enum(['free', 'pro', 'enterprise']),
  /**
   * Customer-facing org label, when one has been set (ASC-7855). Undefined
   * means unset — render `name` instead. Both fields below are optional so an
   * agent bundle built before this existed still validates the context it is
   * handed across the MFE boundary.
   */
  displayName: z.string().optional(),
  /**
   * Whether the org has a logo stored — deliberately NOT the URL. PlatformContext
   * is handed across the MFE boundary once and held for the session, so a signed
   * GET (TTL ~300s) put here would be expired for most of that session's life
   * with nothing able to refresh it. The platform shell resolves the URL through
   * `/api/org/logo/signed-url`; an agent that needs the logo should be handed a
   * fresh URL, never a cached one. Ref: ASC-7855 review.
   */
  hasLogo: z.boolean().optional(),
  /** Cache key for that fetch — changes on every upload. */
  logoUploadedAt: z.string().optional(),
  /**
   * Whether the platform admin has marked this a Showcase org, which enables the
   * shell's "Know Scorpio" walkthrough. Optional for the same reason as the
   * branding fields above. Ref: ASC-9996.
   */
  isShowcase: z.boolean().optional(),
})

export const WorkspaceSchema = z.object({
  /**
   * The workspace's integer surrogate PK (see `WorkspaceIdSchema` in
   * `platform/lib/workspaces-contracts.ts`), carried here as a decimal
   * string — never treat as an opaque token. Call sites that need the
   * numeric PK (e.g. PSA's `execute_sql`) parse it back with
   * `Number()`/`int()`. Ref: ASC-5809.
   */
  id: z.string(),
  name: z.string(),
  type: z.string().optional(),
  description: z.string().optional(),
})

export const AuthStateSchema = z.object({
  user: UserSchema,
  isAuthenticated: z.boolean(),
})

export const AgentSubscriptionSchema = z.object({
  agentId: z.string(),
  enabled: z.boolean(),
  installedAt: z.string().nullable(),
})

/** Org display preference (ASC-10759). Mirrors platform_gateway `DisplayFormat`, minus `defaulted`. */
export const DisplayFormatSchema = z.object({
  /** ISO 4217 code, e.g. 'USD', 'INR'. */
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Fraction digits, 0-4. */
  decimals: z.number().int().min(0).max(4),
  locale: z.enum(['en-US', 'en-IN']),
})
